import type { BoardId, MatchId, TeamId, TournamentId, Uid } from '@/core/schemas';

/**
 * Every Firestore location in one place.
 *
 * All tournament data is nested under `tournaments/{tournamentId}` from day
 * one. That costs nothing today and is what makes "several tournaments at the
 * same time" and "archive of past years" additions rather than migrations.
 */
export const firestorePaths = {
  users: 'users',
  user: (uid: Uid) => `users/${uid}`,

  tournaments: 'tournaments',
  tournament: (tournamentId: TournamentId) => `tournaments/${tournamentId}`,

  teams: (tournamentId: TournamentId) => `tournaments/${tournamentId}/teams`,
  team: (tournamentId: TournamentId, teamId: TeamId) =>
    `tournaments/${tournamentId}/teams/${teamId}`,

  /** Uniqueness guard: the document id is the normalised team name. */
  teamNames: (tournamentId: TournamentId) => `tournaments/${tournamentId}/teamNames`,
  teamName: (tournamentId: TournamentId, nameKey: string) =>
    `tournaments/${tournamentId}/teamNames/${nameKey}`,

  /** Uniqueness guard: one team per captain. */
  captains: (tournamentId: TournamentId) => `tournaments/${tournamentId}/captains`,
  captain: (tournamentId: TournamentId, uid: Uid) => `tournaments/${tournamentId}/captains/${uid}`,

  /** Non-public ownership record: team id → captain UID (see SECURITY.md §6.3). */
  teamOwners: (tournamentId: TournamentId) => `tournaments/${tournamentId}/teamOwners`,
  teamOwner: (tournamentId: TournamentId, teamId: TeamId) =>
    `tournaments/${tournamentId}/teamOwners/${teamId}`,

  matches: (tournamentId: TournamentId) => `tournaments/${tournamentId}/matches`,
  match: (tournamentId: TournamentId, matchId: MatchId) =>
    `tournaments/${tournamentId}/matches/${matchId}`,

  boards: (tournamentId: TournamentId) => `tournaments/${tournamentId}/boards`,
  board: (tournamentId: TournamentId, boardId: BoardId) =>
    `tournaments/${tournamentId}/boards/${boardId}`,
} as const;
